import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui';
import { useTranslation } from '@parentpath/i18n';

type CallbackStatus = 'processing' | 'success' | 'error';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    async function handleAuthCallback() {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const code = searchParams.get('code');
        const type = hashParams.get('type') || searchParams.get('type');
        const errorCode = hashParams.get('error') || searchParams.get('error');
        const errorDescription =
          hashParams.get('error_description') || searchParams.get('error_description');

        if (errorCode) {
          console.error('Auth callback error:', errorCode, errorDescription);
          setStatus('error');
          if (errorDescription?.includes('Unable to exchange external code')) {
            setErrorMessage(t('callback.oauthExchangeError'));
          } else {
            setErrorMessage(errorDescription || t('callback.authError'));
          }
          return;
        }

        // 1. The supabase client may have auto-exchanged the code via
        // detectSessionInUrl before this effect ran. Pick that up first.
        const existing = await supabase.auth.getSession();
        if (existing.error) {
          console.error('Get session error:', existing.error);
          setStatus('error');
          setErrorMessage(t('callback.sessionCheckError'));
          return;
        }
        if (existing.data.session) {
          window.history.replaceState(null, '', window.location.pathname);
          finishSuccess(type);
          return;
        }

        // 2. PKCE code in the query string
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('PKCE exchange error:', error);
            setStatus('error');
            setErrorMessage(t('callback.sessionError'));
            return;
          }
          window.history.replaceState(null, '', window.location.pathname);
          finishSuccess(type);
          return;
        }

        // 3. Implicit-flow hash fallback
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) {
            console.error('Session error:', sessionError);
            setStatus('error');
            setErrorMessage(t('callback.sessionError'));
            return;
          }
          window.history.replaceState(null, '', window.location.pathname);
          finishSuccess(type);
          return;
        }

        // Nothing usable in the URL → bounce to login
        navigate('/login?verified=true', { replace: true });
      } catch (err) {
        console.error('Auth callback exception:', err);
        setStatus('error');
        setErrorMessage(t('callback.unexpectedError'));
      }
    }

    function finishSuccess(type: string | null) {
      if (type === 'recovery') {
        navigate('/reset-password/confirm', { replace: true });
        return;
      }
      setStatus('success');
      const returnTo = searchParams.get('returnTo') || searchParams.get('next');
      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      setTimeout(() => {
        if (returnTo) {
          localStorage.removeItem('pendingInviteToken');
          navigate(returnTo, { replace: true });
        } else if (pendingInviteToken) {
          localStorage.removeItem('pendingInviteToken');
          navigate(`/invite/${pendingInviteToken}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 1500);
    }

    handleAuthCallback();
  }, [navigate, searchParams, t]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('callback.processing')}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('callback.errorTitle')}</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('callback.goToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('callback.successTitle')}</h1>
        <p className="text-gray-600 mb-4">{t('callback.emailConfirmed')}</p>
        <p className="text-sm text-gray-500">{t('callback.redirecting')}</p>
      </div>
    </div>
  );
}
